import React, { useCallback, useRef } from 'react';
import { ACTIONTYPE } from '../AppStateProvider';
import { testSTUNConnectivity } from '../../../utils/stunTesting';

// Safety timeout: 90s total (6 servers × 15s each)
const STUN_TEST_SAFETY_TIMEOUT_MS = 90_000;

export default function useSTUNTest(dispatch: React.Dispatch<ACTIONTYPE>) {
  const stunTestRef = useRef<boolean>(false);
  const cancelledRef = useRef<boolean>(false);
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const startSTUNTest = useCallback(async () => {
    // Don't start a new STUN test if one is already running
    if (stunTestRef.current) {
      return;
    }

    stunTestRef.current = true;
    cancelledRef.current = false;
    dispatch({ type: 'stun-test-started' });

    // Per-test safety timeout
    safetyTimeoutRef.current = setTimeout(() => {
      if (stunTestRef.current && !cancelledRef.current) {
        console.warn('STUN test safety timeout: auto-failing after', STUN_TEST_SAFETY_TIMEOUT_MS, 'ms');
        cancelledRef.current = true;
        stunTestRef.current = false;
        dispatch({ type: 'stun-test-failed', error: new Error('STUN test timed out') });
      }
    }, STUN_TEST_SAFETY_TIMEOUT_MS);

    try {
      const analysis = await testSTUNConnectivity((progress) => {
        if (cancelledRef.current) return; // Stop updating progress after cancel
        if (progress.completed < progress.total) {
          const serverName = progress.currentServer.replace('stun:', '').split(':')[0];
          dispatch({
            type: 'stun-test-progress',
            progress: `Testing ${serverName} (${progress.completed + 1}/${progress.total})`
          });
        } else {
          dispatch({
            type: 'stun-test-progress',
            progress: 'Analyzing NAT type...'
          });
        }
      });

      // Don't dispatch results if cancelled — the cancel handler already transitioned the UI
      if (!cancelledRef.current) {
        dispatch({ type: 'stun-test-completed', analysis });
      }
    } catch (error) {
      if (!cancelledRef.current) {
        dispatch({
          type: 'stun-test-failed',
          error: error instanceof Error ? error : new Error('STUN test failed')
        });
      }
    } finally {
      stunTestRef.current = false;
      if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
    }
  }, [dispatch]);

  const cancelSTUNTest = useCallback(() => {
    cancelledRef.current = true;
    stunTestRef.current = false;
    if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
    // Immediately mark the test as finished so the UI transitions
    dispatch({ type: 'stun-test-failed', error: new Error('Test cancelled') });
  }, [dispatch]);

  return { startSTUNTest, cancelSTUNTest } as const;
}
